import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { ChevronLeft, Mail, Phone, MapPin, Send } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HelpCenterPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    reference: '',
    description: ''
  });

  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    if (!formData.name) {
      toast.error('Please enter your name');
      return false;
    }
    if (!formData.email) {
      toast.error('Please enter your email');
      return false;
    }
    if (!formData.email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.subject) {
      toast.error('Please select a subject');
      return false;
    }
    if (!formData.description) {
      toast.error('Please enter a description');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    try {
      setLoading(true);
      
      const response = await axios.post(`${BACKEND_URL}/api/support/contact`, formData);
      
      if (response.data.success) {
        toast.success('Your message has been sent! We\'ll get back to you within 24-48 hours.');
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          reference: '',
          description: ''
        });
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message. Please try again or email us directly at geostaysindia@gmail.com');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Back to home
          </Button>
          <div className="text-center mb-8">
            <div className="h-16 w-16 rounded-full bg-gradient-to-br from-[#7CB342] to-[#6da036] flex items-center justify-center shadow-lg mx-auto mb-4">
              <Mail className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Help Center</h1>
            <p className="text-gray-600 text-lg">We're here to help! Send us a message and we'll respond within 24-48 hours.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle>Send us a message</CardTitle>
                <CardDescription>
                  Fill out the form below and our support team will get back to you
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Name and Email */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Your Name *</Label>
                      <Input
                        id="name"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={(e) => updateFormData('name', e.target.value)}
                        className="mt-2"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="email">Email Address *</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={(e) => updateFormData('email', e.target.value)}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  {/* Subject and Reference */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="subject">Subject *</Label>
                      <Select value={formData.subject} onValueChange={(value) => updateFormData('subject', value)}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select a subject" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="booking-issue">Booking Issue</SelectItem>
                          <SelectItem value="property-inquiry">Property Inquiry</SelectItem>
                          <SelectItem value="payment-problem">Payment Problem</SelectItem>
                          <SelectItem value="host-support">Host Support</SelectItem>
                          <SelectItem value="account-help">Account Help</SelectItem>
                          <SelectItem value="cancellation">Cancellation Request</SelectItem>
                          <SelectItem value="complaint">Complaint</SelectItem>
                          <SelectItem value="suggestion">Suggestion</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="reference">Reference ID (Optional)</Label>
                      <Input
                        id="reference"
                        placeholder="e.g., Booking ID, Property ID"
                        value={formData.reference}
                        onChange={(e) => updateFormData('reference', e.target.value)}
                        className="mt-2"
                      />
                      <p className="text-xs text-gray-500 mt-1">If applicable, provide booking or property ID</p>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      placeholder="Please describe your issue or question in detail..."
                      value={formData.description}
                      onChange={(e) => updateFormData('description', e.target.value)}
                      className="mt-2 min-h-40"
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum 20 characters</p>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full py-6 text-lg font-semibold bg-[#7CB342] hover:bg-[#6da036] text-white"
                  >
                    {loading ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="h-5 w-5 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Information Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Quick Contact Card */}
            <Card className="bg-green-50 border-green-200">
              <CardHeader>
                <CardTitle className="text-lg">Quick Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Mail className="h-5 w-5 text-[#7CB342] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Email</p>
                    <a href="mailto:geostaysindia@gmail.com" className="text-sm text-gray-700 hover:text-[#7CB342]">
                      geostaysindia@gmail.com
                    </a>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Phone className="h-5 w-5 text-[#7CB342] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Phone</p>
                    <p className="text-sm text-gray-700">+91 (Coming Soon)</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <MapPin className="h-5 w-5 text-[#7CB342] mt-1" />
                  <div>
                    <p className="font-semibold text-sm">Address</p>
                    <p className="text-sm text-gray-700">India</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FAQ Card */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Common Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="font-semibold text-sm mb-1">How long does property approval take?</p>
                  <p className="text-xs text-gray-600">Typically 24-48 hours after submission</p>
                </div>
                <div>
                  <p className="font-semibold text-sm mb-1">Can I modify my booking?</p>
                  <p className="text-xs text-gray-600">Contact the host directly or reach out to us</p>
                </div>
                <div>
                  <p className="font-semibold text-sm mb-1">What's the cancellation policy?</p>
                  <p className="text-xs text-gray-600">Free cancellation 48 hours before check-in</p>
                </div>
              </CardContent>
            </Card>

            {/* Response Time Card */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#7CB342] mb-2">24-48h</div>
                  <p className="text-sm text-gray-700">Average Response Time</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenterPage;
